import "./product-form-options";
import "./product-swatches";
import "./sticky-atc";

/**
 * Reflects the variant announced by product-form-options onto the price,
 * SKU, variant id, add-to-bag button, preorder, waitlist and low-stock notes
 * and URL.
 * An out-of-stock variant with a waitlist swaps add to bag for Notify me,
 * which opens the waitlist modal. One that a store has in stock swaps it for
 * Find In-Store instead, and its waitlist moves to a link under the message.
 */
class ProductForm extends HTMLElement {
  connectedCallback() {
    this.addEventListener("variant:changed", this._onVariantChanged);
    this.addEventListener("click", this._onNotifyClick);
    this.addEventListener("click", this._onFindInStoreClick);
  }

  disconnectedCallback() {
    this.removeEventListener("variant:changed", this._onVariantChanged);
    this.removeEventListener("click", this._onNotifyClick);
    this.removeEventListener("click", this._onFindInStoreClick);
  }

  /**
   * The find-in-store-trigger, absent outside the US, without stores, and
   * behind a CTA override link. product-form-options reads it too.
   */
  get findInStore() {
    return this.querySelector("[data-product-form-find-in-store]")
      ? this.querySelector("find-in-store-trigger")
      : null;
  }

  _onVariantChanged = (event) => {
    const { variant, pendingOption, productLowStock } = event.detail;

    const findInStore =
      !pendingOption &&
      !!variant &&
      !variant.available &&
      !!this.findInStore?.hasStoreStock(variant.id);

    this._updateAtc(variant, pendingOption);
    this._updatePreorder(variant);
    this._updateActions(pendingOption ? null : variant, findInStore);
    this._updateFindInStoreLink(pendingOption ? null : variant);
    this._updateLowStock(
      pendingOption ? productLowStock : variant?.low_stock || 0,
    );

    const idInput = this.querySelector("[data-product-form-id]");
    if (idInput) idInput.value = variant?.id || "";

    if (!variant) return;

    this._updatePrice(variant);
    this._updateSku(variant);
    this._updateUrl(variant);
  };

  _updateAtc(variant, pendingOption) {
    let text;

    if (pendingOption) {
      // An option still to choose
      text = window.theme.translations(this.dataset, "localeSelectOption", {
        name: pendingOption,
      });
    } else if (!variant) {
      // No variant for this combination
      text = this.dataset.localeUnavailable;
    } else if (!variant.available) {
      text = this.dataset.localeSoldOut;
    } else if (variant.preorder) {
      text = this.dataset.localePreOrder;
    } else {
      text = this.dataset.localeAdd;
    }

    this.querySelectorAll("[data-product-form-atc]").forEach((button) => {
      // The sticky button stays live while an option is pending, to take the
      // shopper to it.
      const opensOption = pendingOption && "stickyAtc" in button.dataset;

      button.disabled = !variant?.available && !opensOption;

      const label = button.querySelector("[data-product-form-atc-label]");
      if (label) label.textContent = text;
    });
  }

  // Add to bag, Notify me or Find In-Store; never more than one.
  _updateActions(variant, findInStore) {
    const waitlist = !!variant?.waitlist;

    this.querySelectorAll("[data-product-form-atc]").forEach((atc) => {
      atc.hidden = waitlist || findInStore;
    });
    this.querySelectorAll("[data-product-form-notify]").forEach((notify) => {
      notify.hidden = !waitlist || findInStore;
    });
    this.querySelectorAll("[data-product-form-find-in-store]").forEach(
      (button) => {
        button.hidden = !findInStore;
      },
    );

    const note = this.querySelector("[data-product-form-waitlist]");
    if (note) note.hidden = !waitlist;

    const link = this.querySelector("[data-product-form-waitlist-link]");
    if (link) link.hidden = !(waitlist && findInStore);
  }

  // Hidden for a variant sold out online and in every store, with no
  // waitlist to join.
  _updateFindInStoreLink(variant) {
    const trigger = this.querySelector("find-in-store-trigger");
    if (!trigger) return;

    trigger.hidden =
      !!variant &&
      !variant.available &&
      !variant.waitlist &&
      !trigger.hasStoreStock(variant.id);
  }

  // The product's total while an option is pending, the variant's otherwise.
  _updateLowStock(quantity) {
    const note = this.querySelector("[data-product-form-low-stock]");
    if (!note) return;

    note.hidden = !quantity;
    note.textContent = window.theme.translations(
      this.dataset,
      "localeLowStock",
      {
        quantity,
      },
    );
  }

  // Read on click: a colourway swap morphs in another product's URL, and the
  // hidden id input always holds the selected variant.
  _onNotifyClick = (event) => {
    const notify = event.target.closest(
      "[data-product-form-notify], [data-product-form-waitlist-link]",
    );
    if (!notify) return;

    window.openWaitlist(
      notify,
      this.dataset.waitlistUrl,
      this.querySelector("[data-product-form-id]")?.value,
    );
  };

  _onFindInStoreClick = (event) => {
    const button = event.target.closest("[data-product-form-find-in-store]");
    if (!button) return;

    this.querySelector("find-in-store-trigger")?.open(button);
  };

  // The note and its line item property only apply to a preorder that can
  // be added.
  _updatePreorder(variant) {
    const preorder = variant?.available ? variant.preorder : "";
    const preorderProperty = preorder ? variant.preorder_property : "";

    const note = this.querySelector("[data-product-form-preorder]");
    if (note) {
      note.hidden = !preorder;
      note.textContent = window.theme.translations(
        this.dataset,
        "localePreorderText",
        { text: preorder },
      );
    }

    const property = this.querySelector(
      "[data-product-form-preorder-property]",
    );
    if (property) {
      property.value = preorderProperty;
      property.disabled = !preorder;
    }
  }

  _updatePrice(variant) {
    this.querySelectorAll("[data-product-price]").forEach((price) => {
      price.setAttribute("price", variant.price / 100);
      price.setAttribute("compare-at-price", variant.compare_at_price / 100);
      price.setAttribute("currency", window.Shopify.currency.active);
      price.setAttribute("dynamic", "");
    });
  }

  _updateSku(variant) {
    this.querySelectorAll("[data-product-sku]").forEach((sku) => {
      sku.textContent = variant.sku || "";
    });
  }

  _updateUrl(variant) {
    if (window.Shopify?.designMode) return;

    const url = new URL(window.location.href);
    url.searchParams.set("variant", variant.id);
    history.replaceState(history.state, "", url);
  }
}

if (!customElements.get("product-form")) {
  customElements.define("product-form", ProductForm);
}
